import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import { FormFieldWrapper, type FormFieldWrapperProps } from '../components/FormFieldWrapper';
import type { FormFieldSize } from '../helpers/field-size';
import { Textarea } from './Textarea';

/**
 * TextareaField コンポーネント
 *
 * FieldItem でラップされた Textarea コンポーネント
 * React Hook Form と統合され、ラベル・バリデーション・エラーメッセージを含む
 *
 * 使用例:
 * ```tsx
 * <TextareaField
 *   control={form.control}
 *   name="description"
 *   label="説明"
 *   placeholder="説明を入力してください"
 *   rows={5}
 *   required
 * />
 * ```
 */
type TextareaFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FormFieldWrapperProps<TFieldValues, TName>, 'children'> &
  Omit<React.ComponentProps<'textarea'>, 'name'> & {
    /** フィールドの最小幅（デフォルト: md） */
    size?: FormFieldSize;
  };

export const TextareaField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  supportText,
  showSupportText,
  readOnly,
  displayValue,
  className,
  placeholder,
  rows = 3,
  size = 'sm',
  ...textareaProps
}: TextareaFieldProps<TFieldValues, TName>) => {
  return (
    <FormFieldWrapper
      control={control}
      name={name}
      label={label}
      required={required}
      supportText={supportText}
      showSupportText={showSupportText}
      readOnly={readOnly}
      displayValue={displayValue}
      className={className}
      size={size}
    >
      {({
        field,
        fieldId,
        hasError,
      }: {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldId: string;
        hasError: boolean;
      }) => (
        <Textarea
          {...field}
          {...textareaProps}
          id={fieldId}
          placeholder={placeholder}
          rows={rows}
          size={size}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${fieldId}-error` : undefined}
        />
      )}
    </FormFieldWrapper>
  );
};

TextareaField.displayName = 'TextareaField';
